import type { Rpc } from "@cloudflare/workers-types"
import get from "lodash.get"

export { get }

export function toSerializable<Input>(obj: Input): Rpc.Serializable<Input> {
  return JSON.parse(JSON.stringify(obj)) as Rpc.Serializable<Input>
}

export function isString(value: unknown): value is string {
  return typeof value === "string"
}

type ColumnHeader = {
  key: string
  label: string
}

// Utility function to convert JSON array to CSV
export function jsonToCsv<Row extends Record<string, unknown>>(
  jsonArray: Row[],
  headers: ColumnHeader[],
): string {
  const csvRows = [
    headers
      .map((header) => header.label)
      .join(","), // Add the headers
    ...jsonArray.map((row) =>
      headers
        .map((header) => String(get(row, header.key, "")).replace(/,/g, " "))
        .join(","),
    ),
  ]

  return csvRows.join("\n")
}

export function toBinaryString(arrayBuffer: ArrayBuffer): string {
  const bytes = new Uint8Array(arrayBuffer)
  let binary = ""
  const chunkSize = 1024 // Process 1KB at a time (to avoid RangeError: Maximum call stack size exceeded)

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.slice(i, Math.min(i + chunkSize, bytes.length))
    binary += String.fromCharCode.apply(null, Array.from(chunk))
  }

  return binary
}

export function hexToUint8Array(hex: string): Uint8Array {
  const matched = hex.match(/.{1,2}/g)
  return new Uint8Array(
    matched ? matched.map((byte) => Number.parseInt(byte, 16)) : [],
  )
}

export function toArray<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value]
}

export function safelyExtractEnvVar(
  key: string | string[],
  defaultValue?: string,
) {
  const keys = Array.isArray(key) ? key : [key]

  for (const k of keys) {
    try {
      if (import.meta?.env?.[k]) {
        return import.meta.env[k]
      }

      if (process.env?.[k]) {
        return process.env[k]
      }
    } catch {
      // do nothing
    }
  }

  return defaultValue
}
